import React, { useState } from 'react';
import { kitchenService } from '../../services';
import { toast } from 'react-toastify';

interface Form6Props {
  onNext: (data: Form6Data) => void;
  initialData?: Form6Data;
}

export interface Form6Data {
  restaurantCategory: string;
  seatingCapacity: number;
  indoorSeating: number;
  outdoorSeating: number;
  acAvailable: 'YES' | 'NO' | 'PARTIAL';
  parkingAvailable: boolean;
  valetService: boolean;
  alcoholServed: boolean;
  liveMusic: boolean;
  acceptsTableBookings: boolean;
  qrSelfOrderingEnabled: boolean;
  advanceBookingDays: number;
  cancellationPolicy: 'PARTIAL' | 'FULL' | 'NO_REFUND';
}

const Form6: React.FC<Form6Props> = ({ onNext, initialData }) => {
  const [formData, setFormData] = useState<Form6Data>({
    restaurantCategory: initialData?.restaurantCategory || 'Select Category',
    seatingCapacity: initialData?.seatingCapacity || 0,
    indoorSeating: initialData?.indoorSeating || 0,
    outdoorSeating: initialData?.outdoorSeating || 0,
    acAvailable: initialData?.acAvailable || 'NO',
    parkingAvailable: initialData?.parkingAvailable ?? false,
    valetService: initialData?.valetService ?? false,
    alcoholServed: initialData?.alcoholServed ?? false,
    liveMusic: initialData?.liveMusic ?? false,
    acceptsTableBookings: initialData?.acceptsTableBookings ?? false,
    qrSelfOrderingEnabled: initialData?.qrSelfOrderingEnabled ?? false,
    advanceBookingDays: initialData?.advanceBookingDays || 0,
    cancellationPolicy: initialData?.cancellationPolicy || 'NO_REFUND',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categoryOptions = [
    'Select Category',
    'Fine Dining',
    'Casual Dining',
    'Fast Food',
    'Cafe',
    'Cloud Kitchen',
    'Food Truck',
    'Bakery',
    'Sweet Shop',
  ];

  const acOptions = [
    { label: 'Fully AC', value: 'YES' },
    { label: 'Partially AC', value: 'PARTIAL' },
    { label: 'No AC', value: 'NO' },
  ];

  const cancellationOptions = [
    { label: 'Select Policy', value: '' },
    { label: 'Full Refund', value: 'FULL' },
    { label: 'Partial Refund', value: 'PARTIAL' },
    { label: 'No Refund', value: 'NO_REFUND' },
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value,
    }));
    setError('');
  };

  const handleToggle = (field: keyof Form6Data) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.restaurantCategory || formData.restaurantCategory === 'Select Category') {
      setError('Please select a restaurant category');
      return;
    }
    if (formData.seatingCapacity < 0) {
      setError('Seating capacity must be 0 or greater');
      return;
    }
    if (formData.indoorSeating < 0 || formData.outdoorSeating < 0) {
      setError('Seating values must be 0 or greater');
      return;
    }
    if (formData.advanceBookingDays < 0) {
      setError('Advance booking days must be 0 or greater');
      return;
    }
    if (!formData.cancellationPolicy) {
      setError('Please select a cancellation policy');
      return;
    }

    setLoading(true);
    try {
      // Submit to API
      const response: any = await kitchenService.submitStep6(formData);
      toast.success(response.message || 'Features saved successfully!');
      // Move to next step
      onNext(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save data. Please try again.');
      toast.error(err.message || 'Failed to save features');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="w-full">
        <h2 className="text-[35px] font-Urbanist font-semibold text-gray-900 mb-2 text-left leading-[125%]">
          Additional Details
        </h2>
        <p className="text-[#6A6A6A] text-[15px] font-medium leading-[140%]">
          Enter These details to get started
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-[20px]">
          {/* Restaurant Category */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="restaurantCategory"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Restaurant Category
            </label>
            <select
              id="restaurantCategory"
              name="restaurantCategory"
              value={formData.restaurantCategory}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
              required
            >
              {categoryOptions.map((option) => (
                <option key={option} value={option} disabled={option === 'Select Category'}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Seating Capacity */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="seatingCapacity"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Seating Capacity
            </label>
            <input
              type="number"
              id="seatingCapacity"
              name="seatingCapacity"
              value={formData.seatingCapacity || ''}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter Seating Capacity"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              min="0"
            />
          </div>

          {/* Indoor Seating */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="indoorSeating"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Indoor Seating
            </label>
            <input
              type="number"
              id="indoorSeating"
              name="indoorSeating"
              value={formData.indoorSeating || ''}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter Indoor Seating"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              min="0"
            />
          </div>

          {/* Outdoor Seating */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="outdoorSeating"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Outdoor Seating
            </label>
            <input
              type="number"
              id="outdoorSeating"
              name="outdoorSeating"
              value={formData.outdoorSeating || ''}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter Outdoor Seating"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              min="0"
            />
          </div>

          {/* AC Available */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="acAvailable"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              AC Available?
            </label>
            <select
              id="acAvailable"
              name="acAvailable"
              value={formData.acAvailable}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
              required
            >
              {acOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Parking Available */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-Urbanist font-medium text-[#6A6A6A]">
              Parking Available?
            </label>
            <button
              type="button"
              onClick={() => handleToggle('parkingAvailable')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.parkingAvailable ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.parkingAvailable ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Valet Service */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-Urbanist font-medium text-[#6A6A6A]">
              Valet Service?
            </label>
            <button
              type="button"
              onClick={() => handleToggle('valetService')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.valetService ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.valetService ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Alcohol Served */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-Urbanist font-medium text-[#6A6A6A]">
              Alcohol Served?
            </label>
            <button
              type="button"
              onClick={() => handleToggle('alcoholServed')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.alcoholServed ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.alcoholServed ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Live Music */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-Urbanist font-medium text-[#6A6A6A]">
              Live Music?
            </label>
            <button
              type="button"
              onClick={() => handleToggle('liveMusic')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.liveMusic ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.liveMusic ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Accepts Table Bookings */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-Urbanist font-medium text-[#6A6A6A]">
              Accepts Table Bookings?
            </label>
            <button
              type="button"
              onClick={() => handleToggle('acceptsTableBookings')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.acceptsTableBookings ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.acceptsTableBookings ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* QR Self Ordering Enabled */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-Urbanist font-medium text-[#6A6A6A]">
              QR Self Ordering Enabled?
            </label>
            <button
              type="button"
              onClick={() => handleToggle('qrSelfOrderingEnabled')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.qrSelfOrderingEnabled ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.qrSelfOrderingEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Advance Booking Days */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="advanceBookingDays"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Advance Booking Days
            </label>
            <input
              type="number"
              id="advanceBookingDays"
              name="advanceBookingDays"
              value={formData.advanceBookingDays || ''}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter Advance Booking Days"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              min="0"
            />
          </div>

          {/* Cancellation Policy */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="cancellationPolicy"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Cancellation Policy
            </label>
            <select
              id="cancellationPolicy"
              name="cancellationPolicy"
              value={formData.cancellationPolicy}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
              required
            >
              {cancellationOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={!option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="w-full px-6 py-3.5 text-base font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Next'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form6;